import {
  Divider,
  Grid,
  Typography,
  TextField,
  Button,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Alert,
  Paper,
  Container,
  IconButton,
  CircularProgress,
  Box,
} from "@mui/material";
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import Error from "./Error";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { db } from "../firebase";
import LogoutIcon from "@mui/icons-material/Logout";

export default function Dashboard() {
  const [user, setUser] = useState(null);
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [success, setSuccess] = useState(false);

  // Common styling for all text fields
  const textFieldSx = {
    mb: 2,
    input: { color: "white" },
    "& .MuiInputLabel-root": { color: "grey" },
    "& .MuiOutlinedInput-root": {
      "& fieldset": {
        borderColor: "#fff",
      },
      "&:hover fieldset": {
        borderColor: "#fff",
      },
      "&.Mui-focused fieldset": {
        borderColor: "#fff",
      },
    },
  };

  const [formData, setFormData] = useState({
    name: "",
    mobileNumber: "",
    age: "",
    email: "",
    parentName: "",
    parentMobile: "",
    schoolName: "",
    schoolCity: "",
    schoolAddress: "",
    pool: "",
    class: "",
  });

  const [classOptions, setClassOptions] = useState([]);

  const navigate = useNavigate();
  const { currentUser, logout } = useAuth();

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Failed to log out", error);
      setError("Failed to log out. Please try again.");
    }
  };

  // Class options based on pool selection
  const poolClasses = {
    A: ["Class 6", "Class 7", "Class 8"],
    B: ["Class 9", "Class 10"],
    C: ["Class 11", "Class 12"],
  };

  useEffect(() => {
    async function fetchUserData() {
      if (!currentUser) {
        navigate("/login");
        return;
      }

      try {
        const userDocRef = doc(db, "students", currentUser.uid);
        const userDoc = await getDoc(userDocRef);

        if (userDoc.exists()) {
          const userData = userDoc.data();
          setUser(userData);
          setFormData({
            name: userData.name || "",
            mobileNumber: userData.mobileNumber || "",
            age: userData.age || "",
            email: userData.email || currentUser.email || "",
            parentName: userData.parentName || "",
            parentMobile: userData.parentMobile || "",
            schoolName: userData.schoolName || "",
            schoolCity: userData.schoolCity || "",
            schoolAddress: userData.schoolAddress || "",
            pool: userData.pool || "",
            class: userData.class || "",
            profilePicture: userData.profilePicture || "",
          });

          // Set class options if pool is already selected
          if (userData.pool && poolClasses[userData.pool]) {
            setClassOptions(poolClasses[userData.pool]);
          }
        } else {
          // Set initial form data for new user
          setFormData({
            name: "",
            mobileNumber: "",
            age: "",
            email: currentUser.email || "",
            parentName: "",
            parentMobile: "",
            schoolName: "",
            schoolCity: "",
            schoolAddress: "",
            pool: "",
            class: "",
          });
        }
      } catch (err) {
        console.error("Error fetching user data:", err);
        setError("Error fetching user data. Please try again.");
      } finally {
        setLoading(false);
      }
    }

    fetchUserData();
  }, [currentUser, navigate]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;

    if (name === "pool") {
      // Update class options when pool changes
      setClassOptions(poolClasses[value] || []);
      // Reset class selection
      setFormData({
        ...formData,
        [name]: value,
        class: "",
      });
    } else {
      setFormData({
        ...formData,
        [name]: value,
      });
    }
  };

  // Check if all required fields are filled
  const isFormComplete = () => {
    return (
      formData.name &&
      formData.mobileNumber &&
      formData.age &&
      formData.email &&
      formData.parentName &&
      formData.parentMobile &&
      formData.schoolName &&
      formData.schoolCity &&
      formData.schoolAddress &&
      formData.pool &&
      formData.class
    );
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!isFormComplete()) {
      setError("Please fill in all required fields.");
      return;
    }

    try {
      setLoading(true);
      const userDocRef = doc(db, "students", currentUser.uid);

      const updatedFormData = {
        ...formData,
      };

      await setDoc(
        userDocRef,
        {
          ...updatedFormData,
          uid: currentUser.uid,
          updatedAt: new Date(),
          createdAt: user?.createdAt || new Date(),
        },
        { merge: true }
      );

      setUser({
        ...user,
        ...updatedFormData,
      });

      setFormData(updatedFormData);

      setEditing(false);
      setError(false);
      setSuccess("Profile updated successfully!");

      // Hide success message after 3 seconds
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      console.error("Error updating profile:", err);
      setError("Failed to update profile. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Toggle edit mode
  const toggleEdit = () => {
    setEditing(!editing);
    setError(false);
    setSuccess(false);
  };

  return error ? (
    <Error
      message={error}
      solution="Please make sure you are connected to Internet."
    />
  ) : loading ? (
    <Box sx={{ display: "block", textAlign: "center", marginTop: "100px" }}>
      <CircularProgress />
    </Box>
  ) : (
    <Container maxWidth="md" sx={{ px: { xs: 2, sm: 3 } }}>
      <Paper
        elevation={3}
        sx={{ p: { xs: 3, sm: 4 }, mt: 4, borderRadius: 2, bgcolor: "#1a1a2e" }}
      >
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 3,
          }}
        >
          <Typography
            variant="h4"
            sx={{
              color: "#fff",
              fontSize: { xs: "1.75rem", sm: "2rem" },
            }}
          >
            {editing
              ? "Complete Your Profile"
              : "Welcome " + (formData.name || currentUser.email)}
          </Typography>
          {/* <IconButton
            onClick={handleLogout}
            sx={{ color: "#fff" }}
            aria-label="logout"
          >
            <LogoutIcon />
          </IconButton> */}
        </Box>

        {!user?.name && !editing && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            Please complete your profile.
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 3 }}>
            {success}
          </Alert>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {editing ? (
          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              {/* Personal Information */}
              <Grid item xs={12}>
                <Typography variant="h6" sx={{ color: "#76ddff", mb: 2 }}>
                  Personal Information
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Full Name"
                  name="name"
                  value={formData.name}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Mobile Number"
                  name="mobileNumber"
                  value={formData.mobileNumber}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Age"
                  name="age"
                  type="number"
                  value={formData.age}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Email"
                  name="email"
                  value={formData.email}
                  disabled
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              {/* Parent/Guardian Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }}>
                  <Typography sx={{ color: "white" }}>
                    Parent/Guardian Information
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Parent/Guardian Name"
                  name="parentName"
                  value={formData.parentName}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="Parent/Guardian Mobile No."
                  name="parentMobile"
                  value={formData.parentMobile}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              {/* School/Coaching Information */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }}>
                  <Typography sx={{ color: "white" }}>
                    School/Coaching Information
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School/Coaching Name"
                  name="schoolName"
                  value={formData.schoolName}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  required
                  label="School/Coaching City"
                  name="schoolCity"
                  value={formData.schoolCity}
                  onChange={handleInputChange}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              <Grid item xs={12}>
                <TextField
                  fullWidth
                  required
                  label="School/Coaching Address"
                  name="schoolAddress"
                  value={formData.schoolAddress}
                  onChange={handleInputChange}
                  multiline
                  rows={3}
                  variant="outlined"
                  sx={textFieldSx}
                />
              </Grid>

              {/* Pool and Class Selection */}
              <Grid item xs={12}>
                <Divider sx={{ my: 2, borderColor: "rgba(255,255,255,0.3)" }}>
                  <Typography sx={{ color: "white" }}>
                    Academic Information
                  </Typography>
                </Divider>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={textFieldSx}>
                  <InputLabel sx={{ color: "grey" }}>Select Pool</InputLabel>
                  <Select
                    required
                    name="pool"
                    value={formData.pool}
                    onChange={handleInputChange}
                    label="Select Pool"
                    sx={{ color: "white" }}
                  >
                    <MenuItem value="A">Pool A (Class 6th, 7th, 8th)</MenuItem>
                    <MenuItem value="B">Pool B (Class 9th, 10th)</MenuItem>
                    <MenuItem value="C">Pool C (Class 11th, 12th)</MenuItem>
                  </Select>
                </FormControl>
                <Typography
                  variant="caption"
                  sx={{ color: "lightgrey", ml: 1 }}
                >
                  {formData.pool === "A" && "Pool A: Classes 6th, 7th, and 8th"}
                  {formData.pool === "B" && "Pool B: Classes 9th and 10th"}
                  {formData.pool === "C" && "Pool C: Classes 11th and 12th"}
                </Typography>
              </Grid>

              <Grid item xs={12} sm={6}>
                <FormControl fullWidth sx={textFieldSx}>
                  <InputLabel sx={{ color: "grey" }}>Select Class</InputLabel>
                  <Select
                    required
                    name="class"
                    value={formData.class}
                    onChange={handleInputChange}
                    label="Select Class"
                    disabled={!formData.pool}
                    sx={{ color: "white" }}
                  >
                    {classOptions.map((classOption) => (
                      <MenuItem key={classOption} value={classOption}>
                        {classOption}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>

            <Box sx={{ display: "flex", gap: 2, mt: 3 }}>
              <Button
                type="submit"
                variant="contained"
                sx={{ flex: 1 }}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : "Submit Profile"}
              </Button>
              <Button
                variant="outlined"
                sx={{ flex: 1, color: "white", borderColor: "white" }}
                onClick={toggleEdit}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </form>
        ) : (
          <Box>
            <Box sx={{ p: 2, borderRadius: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>Name: </strong>{" "}
                    {user?.name || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      Mobile Number:{" "}
                    </strong>{" "}
                    {user?.mobileNumber || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>Age: </strong>{" "}
                    {user?.age || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>Email: </strong>{" "}
                    {user?.email}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      Parent/Guardian Name:{" "}
                    </strong>{" "}
                    {user?.parentName || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      Parent/Guardian Mobile:{" "}
                    </strong>{" "}
                    {user?.parentMobile || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      School/Coaching Name:{" "}
                    </strong>{" "}
                    {user?.schoolName || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      School/Coaching City:{" "}
                    </strong>{" "}
                    {user?.schoolCity || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>
                      School/Coaching Address:{" "}
                    </strong>{" "}
                    {user?.schoolAddress || "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>Pool: </strong>{" "}
                    {user?.pool ? `Pool ${user.pool}` : "Not set"}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography sx={{ color: "white", mb: 2 }}>
                    <strong style={{ color: "#76ddff" }}>Class: </strong>{" "}
                    {user?.class || "Not set"}
                  </Typography>
                </Grid>
              </Grid>

              <Button
                variant="contained"
                color="primary"
                onClick={toggleEdit}
                sx={{ mt: 3, width: "100%" }}
              >
                {user?.name ? "Update Profile" : "Complete Profile"}
              </Button>
            </Box>
          </Box>
        )}
      </Paper>
    </Container>
  );
}
